/**
 * L'agenda du jour — côté écran.
 *
 * La journée se borne en heure locale (`dayBounds`) : un rendez-vous à 0 h 30
 * ne doit pas glisser la veille parce que le serveur compte en UTC.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ZodError } from "zod";
import { DomainError, dayBounds, saveAppointmentInput } from "@dker/core";
import type { SaveAppointmentFormInput } from "@dker/core";
import { agendaServices, serviceDeps, useExecutionContext } from "@/services";
import { useTenantOptional } from "@/hooks/useTenant";
import { useUiStore } from "@/stores/uiStore";

function toMessage(error: unknown): string {
  if (error instanceof DomainError) return error.message;
  if (error instanceof ZodError) return error.issues[0]?.message ?? "Saisie incomplète.";
  return "Une erreur inattendue est survenue. Réessayez dans un instant.";
}

export function useAgendaOfDay(day: Date = new Date()) {
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const { from, to } = dayBounds(day);

  return useQuery({
    queryKey: ["agenda", tenant?.id, from],
    enabled: !!tenant,
    queryFn: () => agendaServices.listerRendezVous(makeContext(), { from, to }, serviceDeps),
  });
}

export function useSaveAppointment() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    // Les services ne valident pas leur entrée : c'est l'écran qui applique le
    // schéma, comme le formulaire de devis le fait par son résolveur.
    mutationFn: (input: SaveAppointmentFormInput) =>
      agendaServices.enregistrerRendezVous(
        makeContext(),
        saveAppointmentInput.parse(input),
        serviceDeps,
      ),
    onSuccess: (_r, input) => {
      void qc.invalidateQueries({ queryKey: ["agenda", tenant?.id] });
      addToast({ type: "success", title: input.id ? "Rendez-vous déplacé" : "Rendez-vous noté" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Rendez-vous non enregistré", message: toMessage(e) }),
  });
}

export function useRemoveAppointment() {
  const qc = useQueryClient();
  const tenant = useTenantOptional();
  const makeContext = useExecutionContext();
  const addToast = useUiStore((s) => s.addToast);

  return useMutation({
    mutationFn: (id: string) =>
      agendaServices.supprimerRendezVous(makeContext(), { id }, serviceDeps),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["agenda", tenant?.id] });
      addToast({ type: "info", title: "Rendez-vous supprimé" });
    },
    onError: (e) =>
      addToast({ type: "error", title: "Suppression impossible", message: toMessage(e) }),
  });
}
